import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border font-medium leading-none transition-colors duration-[80ms] hover:no-underline disabled:cursor-not-allowed disabled:shadow-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border-border bg-secondary text-secondary-foreground shadow-btn hover:bg-secondary-hover active:bg-accent disabled:text-faint disabled:hover:bg-secondary [&_svg]:text-muted-foreground",
        primary:
          "border-border-translucent bg-primary text-primary-foreground shadow-resting hover:bg-primary-hover disabled:bg-primary-disabled disabled:text-primary-disabled-foreground disabled:hover:bg-primary-disabled",
        danger:
          "border-border bg-secondary text-danger shadow-btn hover:border-border-translucent hover:bg-destructive-hover hover:text-destructive-foreground hover:shadow-resting active:bg-destructive-active disabled:text-faint disabled:hover:border-border disabled:hover:bg-secondary disabled:hover:text-faint",
        ghost: "border-transparent bg-transparent text-foreground hover:bg-transparent-hover active:bg-transparent-active disabled:text-faint disabled:hover:bg-transparent [&_svg]:text-muted-foreground",
      },
      size: {
        default: "h-8 px-3 text-sm",
        sm: "h-7 px-2 text-xs",
        icon: "size-8",
        "icon-sm": "size-6 border-0 text-muted-foreground hover:text-foreground [&_svg]:text-current",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button";
  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
